import { useState, useEffect } from 'react'
import { Shield, Loader, AlertCircle, CheckCircle } from 'lucide-react'

interface PlatformAdminLoginProps {
  onLoginSuccess: () => void
}

const PlatformAdminLogin = ({ onLoginSuccess }: PlatformAdminLoginProps) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  // Initialize Google Identity Services
  useEffect(() => {
    // Wait for GIS library to load
    if (typeof window.google === 'undefined') {
      console.warn('Google Identity Services SDK not loaded yet')
      return
    }

    try {
      // Get Google Client ID from environment
      const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID
      const isDevelopment = import.meta.env.DEV || window.location.hostname === 'localhost'
      const finalClientId = clientId || (isDevelopment ? '406298590928-2p9o3mjhfj6e8jb3rjd9lf44iivtfhqm.apps.googleusercontent.com' : '')

      if (!finalClientId) {
        console.error('❌ VITE_GOOGLE_CLIENT_ID not configured')
        setError('Google Sign-In is not configured. Please contact your administrator.')
        return
      }

      if (!clientId && isDevelopment) {
        console.warn('⚠️ Using development Google Client ID')
      }

      console.log('🔐 Initializing Google Identity Services for Platform Admin...')

      // Cancel any existing One Tap flows (from tenant login)
      window.google.accounts.id.cancel()

      // Initialize GIS with FedCM support - this will override any previous initialization
      window.google.accounts.id.initialize({
        client_id: finalClientId,
        callback: handleGoogleCredentialResponse,
        auto_select: true,
        cancel_on_tap_outside: true,
        use_fedcm_for_prompt: true
      })

      // Render the Sign In with Google button
      const buttonElement = document.getElementById('platform-admin-google-signin')
      if (buttonElement) {
        window.google.accounts.id.renderButton(buttonElement, {
          theme: 'outline',
          size: 'large',
          width: 400,
          text: 'continue_with',
          shape: 'rectangular',
          logo_alignment: 'left'
        })
        console.log('✅ Platform Admin Google Sign-In button rendered')
      }

      // Show One Tap for seamless sign-in
      const timerId = setTimeout(() => {
        console.log('🎯 Attempting to show One Tap for Platform Admin...')
        window.google.accounts.id.prompt((notification: any) => {
          if (notification.isNotDisplayed()) {
            console.warn('⚠️ One Tap not displayed:', notification.getNotDisplayedReason())
          } else if (notification.isSkippedMoment()) {
            console.log('⏭️ One Tap skipped:', notification.getSkippedReason())
          } else if (notification.isDismissedMoment()) {
            console.log('❌ One Tap dismissed:', notification.getDismissedReason())
          } else {
            console.log('✅ One Tap displayed successfully')
          }
        })
      }, 100)

      return () => clearTimeout(timerId)
    } catch (err) {
      console.error('❌ Failed to initialize Google Sign-In:', err)
    }
  }, [])

  const handleGoogleCredentialResponse = async (response: any) => {
    try {
      setLoading(true)
      setError(null)
      setSuccess('Verifying with Google...')

      // Send the credential (ID token) to our backend for verification
      const verifyResponse = await fetch('/api/platform-admin/auth/google/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          credential: response.credential // This is the JWT ID token from Google
        })
      })

      const data = await verifyResponse.json()

      if (verifyResponse.ok) {
        setSuccess('Login successful! Redirecting...')
        setTimeout(() => {
          onLoginSuccess()
        }, 500)
      } else {
        setError(data.error || 'Google sign-in failed')
        setLoading(false)
      }
    } catch (err: any) {
      console.error('Platform Admin Google sign-in error:', err)
      setError(err.message || 'Failed to sign in with Google')
      setLoading(false)
    }
  }

  // Check for error in URL params (from OAuth callback - legacy)
  const urlParams = new URLSearchParams(window.location.search)
  const urlError = urlParams.get('error')

  const getErrorMessage = (error: string): string => {
    switch (error) {
      case 'domain_not_allowed':
        return 'Your email domain is not authorized for platform admin access. Only @trustcenterpage.com and @trustcenter.page emails are allowed.'
      case 'not_authorized':
        return 'You are not authorized as a platform admin. Please contact your administrator.'
      case 'invalid_state':
        return 'Invalid authentication state. Please try again.'
      case 'auth_failed':
        return 'Authentication failed. Please try again.'
      case 'access_denied':
        return 'You denied access to your account.'
      default:
        return 'An error occurred during authentication. Please try again.'
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl mb-4 shadow-lg">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Platform Admin</h1>
          <p className="text-gray-600">Manage all trust center tenants</p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8">
          {/* Error Messages */}
          {(urlError || error) && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-900">Authentication Error</p>
                <p className="text-sm text-red-700 mt-1">
                  {urlError ? getErrorMessage(urlError) : error}
                </p>
              </div>
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-green-900">{success}</p>
              </div>
            </div>
          )}

          {/* Login Instructions */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-900">
              <strong>Platform admin access</strong> is restricted to authorized @trustcenterpage.com and @trustcenter.page accounts.
            </p>
          </div>

          {/* Google Identity Services Button - Rendered by GIS SDK */}
          {loading ? (
            <div className="flex items-center justify-center py-3">
              <Loader className="w-6 h-6 animate-spin text-blue-600 mr-3" />
              <span className="text-gray-600">Signing in...</span>
            </div>
          ) : (
            <div
              id="platform-admin-google-signin"
              className="flex justify-center"
              style={{ minHeight: '44px' }}
            />
          )}

          {/* SSO Notice */}
          <p className="text-xs text-gray-500 text-center mt-4">
            Powered by Google Identity Services with FedCM
          </p>

          {/* Security Notice */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-xs text-gray-500 text-center">
              By signing in, you agree to our security policies and acknowledge that all actions are logged for audit purposes.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            Need help? Contact{' '}
            <a href="mailto:support@trustcenterpage.com" className="text-blue-600 hover:text-blue-700 font-medium">
              support@trustcenterpage.com
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}

export default PlatformAdminLogin
